import {Box, ButtonGroup, Grid, Typography} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import {usePrevious} from '../../hooks/usePrevious';
import {useDummyHandoverNotes} from '../../hooks/useDummyHandoverNotes';
import {GetHandoverNotes} from '../../schema-types/GetHandoverNotes';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {
  FormikNotesValues,
  HandoverNoteFields,
  getNoHardNotesRequired,
  getReviewedValueFromField,
  notesOrder,
  useOnUpdateNoteMutation,
} from './HandoverNotesHelpers';
import {NotesEditorHandoverV1} from './NotesEditorHandover';
import {NotesSideMenu} from './NotesSideMenu';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {FeatureBoundary, hasPermission} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {NotesEditorHandoverV2, useIsAtrReadOnly} from './NotesEditorV2';
import {useDjangoContext} from '../../contexts/useAuthContext';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import HardFactsDummy from './HardFactsDummy';
import {IncomeExpenditureTable} from '../../features/notes/IncomeExpenditureTable/IncomeExpenditureTable';
import {LoansLiabilitiesTable} from '../../features/notes/LoansLiabilitiesTable/LoansLiabilitiesTable';
import {RetirementPlanningTable} from '../../features/notes/RetirementPlanning/RetirementPlanningTable';
import {SavingsInvestimentsTable} from '../../features/notes/SavingsInvestimentsTable/SavingsInvestimentsTable';
import {Button} from '../../components/Button/Button';

type formProps = {
  reviewId: string;
  readOnly: boolean;
  handoverData: GetHandoverNotes;
  setCurrentNotesType: React.Dispatch<React.SetStateAction<HandoverNoteFields>>;
  currentNotesType: HandoverNoteFields;
  _onUpdateNote: ReturnType<typeof useOnUpdateNoteMutation>['_onUpdateNote'];
  incrementRefreshKey: () => void;
};

const getHardFactsComponent = (currentNotesType: HandoverNoteFields) => {
  switch (currentNotesType) {
    case 'Income & Expenditure':
      return <IncomeExpenditureTable />;
    case 'Loans & Liabilities':
      return <LoansLiabilitiesTable />;
    case 'Retirement planning':
      return <RetirementPlanningTable />;
    case 'Saving & Investment':
      return <SavingsInvestimentsTable />;
    default:
      return <HardFactsDummy />;
  }
};

export const HandoverNotesInner: React.FC<formProps> = ({
  reviewId: _reviewId,
  handoverData,
  currentNotesType,
  setCurrentNotesType,
  _onUpdateNote,
  readOnly,
  incrementRefreshKey,
}) => {
  const {setFieldTouched} = useAutoForm();
  const {values, submitForm} = useAutoForm<FormikNotesValues>();
  const prevNotesType = usePrevious(currentNotesType);
  const {djangoUser} = useDjangoContext();
  const [tab, setTab] = useState<'hard facts' | 'soft notes'>('soft notes');

  const {handleUseDummyData, isDummyDataLoading} = useDummyHandoverNotes({
    readOnly,
    incrementRefreshKey,
  });

  const noHardNotesRequired = useMemo(() => {
    return getNoHardNotesRequired(currentNotesType);
  }, [currentNotesType]);
  const onUpdateNote = (noteType: HandoverNoteFields) => {
    if (readOnly) {
      return;
    }
    const note = values[noteType];
    if (!note) {
      return;
    }
    _onUpdateNote({
      variables: {
        noteId: note.id,
        name: note.name,
        body: note.reviewed ? '' : note.body,
        reviewed: getReviewedValueFromField(note),
      },
    });
  };

  const itemArray = useMemo(() => Object.values(values), [JSON.stringify(values)]);

  const requiredNotes = useMemo(() => {
    const filteredNotes = itemArray.filter((i) => i.required);
    const sortedNotes = filteredNotes.sort((a, b) => {
      const indexA = notesOrder
        .map((item) => item.toLowerCase())
        .indexOf(a.displayName.toLowerCase());
      const indexB = notesOrder
        .map((item) => item.toLowerCase())
        .indexOf(b.displayName.toLowerCase());
      return indexA - indexB;
    });

    return sortedNotes;
  }, [itemArray]);
  const optionalNotes = useMemo(() => {
    return itemArray
      .filter((i) => !i.required)
      .sort(
        (a, b) => notesOrder.indexOf(a.displayName) - notesOrder.indexOf(b.displayName)
      );
  }, [itemArray]);

  useEffect(() => {
    return () => {
      if (!readOnly) {
        submitForm();
      }
    };
  }, [submitForm, readOnly]);

  useEffect(() => {
    if (noHardNotesRequired) {
      setTab('soft notes');
    }
  }, [noHardNotesRequired]);

  useEffect(() => {
    if (readOnly || !prevNotesType || prevNotesType === currentNotesType) return;

    const note = values[prevNotesType];
    if (!note) {
      console.warn(`Note for type ${prevNotesType} is undefined`);
      return;
    }

    setFieldTouched(`${prevNotesType}.reviewed`, true);

    // eslint-disable-next-line consistent-return
    return () => {
      if (prevNotesType !== currentNotesType && note) {
        _onUpdateNote({
          variables: {
            noteId: note.id,
            name: note.name,
            body: note.reviewed ? null : note.body,
            markupBody: note.reviewed ? null : note.markupBody,
            reviewed: getReviewedValueFromField(note),
          },
        });
      }
    };
  }, [prevNotesType, currentNotesType, values, setFieldTouched, _onUpdateNote, readOnly]);

  const isAtrReadOnly = useIsAtrReadOnly('Attitude to risk', handoverData);

  return (
    <Box p={1} pl={0} pr={0}>
      <FeatureBoundary
        showErrorIfDenied={false}
        features={[GlobalPermissionEnum.can_access_admin_features]}
      >
        <Box mb={2}>
          <Button
            eventName="use-dummy-data"
            variant="outlined"
            color="primary"
            onClick={handleUseDummyData}
            disabled={readOnly || isDummyDataLoading}
            sx={{mb: 2}}
          >
            {isDummyDataLoading ? 'Loading...' : 'Use dummy data'}
          </Button>
        </Box>
      </FeatureBoundary>

      <ShowIfDebugWrapper>prevNote: {prevNotesType}</ShowIfDebugWrapper>

      <Grid container spacing={2} className="fixed-footer-gap">
        <Grid item xs={2.5}>
          <NotesSideMenu
            readOnly={readOnly}
            isAtrReadOnly={isAtrReadOnly}
            onUpdateNote={readOnly ? () => {} : onUpdateNote}
            requiredNotes={requiredNotes}
            optionalNotes={optionalNotes}
            currentNotesType={currentNotesType}
            setPage={setCurrentNotesType}
          />
        </Grid>
        <Grid item xs={9.5} sx={{position: 'relative'}}>
          <Box mb={2} display="flex" justifyContent="flex-start" alignItems="center">
            <Typography variant="h4" color="primary">
              <strong>
                <span style={{textTransform: 'capitalize'}}>{currentNotesType}</span>{' '}
                notes
              </strong>
            </Typography>
            {noHardNotesRequired ? null : (
              <ButtonGroup
                variant="outlined"
                size="small"
                sx={{ml: 2}}
                aria-label="outlined button group"
              >
                <Button
                  eventName="notes-tab-hard-facts"
                  variant={tab === 'soft notes' ? 'contained' : 'outlined'}
                  onClick={() => setTab('soft notes')}
                >
                  Soft notes
                </Button>
                <Button
                  eventName="notes-tab-hard-facts"
                  variant={tab === 'hard facts' ? 'contained' : 'outlined'}
                  onClick={() => setTab('hard facts')}
                >
                  Hard facts
                </Button>
              </ButtonGroup>
            )}
          </Box>
          {tab === 'soft notes' ? (
            <>
              <FeatureBoundary
                showErrorIfDenied={false}
                features={[GlobalPermissionEnum.can_access_notes]}
              >
                {hasPermission(djangoUser, [
                  GlobalPermissionEnum.can_access_notes_v2,
                ]) ? null : (
                  <NotesEditorHandoverV1
                    _onUpdateNote={_onUpdateNote}
                    readOnly={readOnly}
                    currentNotesType={currentNotesType}
                    handoverData={handoverData}
                  />
                )}
              </FeatureBoundary>
              <FeatureBoundary
                showErrorIfDenied={false}
                features={[GlobalPermissionEnum.can_access_notes_v2]}
              >
                <NotesEditorHandoverV2
                  _onUpdateNote={_onUpdateNote}
                  readOnly={readOnly}
                  currentNotesType={currentNotesType}
                  handoverData={handoverData}
                  key={currentNotesType}
                />
              </FeatureBoundary>
            </>
          ) : (
            <>{noHardNotesRequired ? null : getHardFactsComponent(currentNotesType)}</>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export const HandoverNotesForm = withAutoFormNew<FormikNotesValues, formProps>(
  HandoverNotesInner
);
